import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NgxFileDropEntry } from 'ngx-file-drop';
import { catchError, map, Observable, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ImgUpload, UploadValidationError } from '../model/img-upload';
import {
    SignedUrlResponse,
    UploadProcessingSessionStatusResponse,
} from '../model/signed-url';
import {
    createFreePlanImageLimitExceededErrorDialogData,
    createFreePlanUploadSelectionLimitExceededDialogData,
    MAT_DIALOG_CONFIG_DATA_INSUFFICIENT_CREDIT_AMOUNT_ERROR,
} from 'src/app/shared/shared-error-dialog-data';
import {
    ConfirmationDialogComponent,
    ConfirmationDialogData,
} from 'src/app/shared/components/confirmation-modal/confirmation-dialog.component';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';

export interface ValidationResult {
    validFiles: File[];
    errors: UploadValidationError[];
}

export const FREE_PLAN_IMAGE_LIMIT = 100;
export const FREE_PLAN_IMAGE_LIMIT_EXCEEDED_ERROR_CODE =
    'FREE_PLAN_IMAGE_LIMIT_EXCEEDED';

@Injectable()
export class ImgDbUploadUtilService {
    private readonly MAX_FILE_SIZE: number = 10485760;
    private readonly MIN_FILE_SIZE: number = 0;

    readonly restUrl: string;
    readonly VALID_FILE_FORMATS: string[] = ['image/jpeg'];
    private matDialogConfig: MatDialogConfig<ConfirmationDialogData>;

    constructor(
        private http: HttpClient,
        private matDialog: MatDialog
    ) {
        this.restUrl = `${environment.backendUrl}/rest/img-db`;
        this.matDialogConfig = new MatDialogConfig();
    }

    generateSignedUrls(
        files: File[],
        imgDbId: string,
        uploadSessionId: string
    ): Observable<ImgUpload[]> {
        const fileNames = files.map((file) => file.name);
        return this.http
            .post<SignedUrlResponse>(`${this.restUrl}s/${imgDbId}/signed`, {
                names: fileNames,
                uploadSessionId,
            })
            .pipe(
                map((urls) => this.mapToUrls(urls.urlMap, files)),
                catchError((error) => {
                    if (
                        error?.error?.type ===
                        'InsufficientCreditAmountException'
                    ) {
                        this.openErrorDialog(
                            MAT_DIALOG_CONFIG_DATA_INSUFFICIENT_CREDIT_AMOUNT_ERROR
                        );
                    } else if (
                        error?.error?.errorCode ===
                        FREE_PLAN_IMAGE_LIMIT_EXCEEDED_ERROR_CODE
                    ) {
                        this.showFreePlanImageLimitExceededError(
                            error?.error?.remainingImageCount ?? 0
                        );
                    }
                    return of([]);
                })
            );
    }

    hasActiveUploadProcessingSession(
        uploadSessionId: string
    ): Observable<boolean> {
        return this.http
            .get<UploadProcessingSessionStatusResponse>(
                `${this.restUrl}s/upload-processing/${uploadSessionId}/active`
            )
            .pipe(map((response) => response.active));
    }

    private mapToUrls(urls: Map<string, string>, files: File[]): ImgUpload[] {
        let imageUploadObj: ImgUpload[] = [];
        for (let i = 0; i < files.length; i++) {
            let file = files[i];
            imageUploadObj.push({ url: urls[file.name], file: file });
        }
        return imageUploadObj;
    }

    async validateFiles(
        droppedFiles: NgxFileDropEntry[]
    ): Promise<ValidationResult> {
        const validatedFiles: File[] = [];
        const errors: UploadValidationError[] = [];

        for (const file of droppedFiles) {
            // Check the file is not directory
            if (!file.fileEntry.isFile) {
                errors.push({
                    fileName: file.fileEntry.name,
                    reason: 'invalidFormat',
                });
                continue;
            }
            const respFile = await this.getFileFromFileEntry(
                file.fileEntry as FileSystemFileEntry
            );
            const fileErrors = this.validateFileBeforeUpload(respFile);
            if (fileErrors.length === 0) {
                validatedFiles.push(respFile);
            } else {
                errors.push(...fileErrors);
            }
        }

        return { validFiles: validatedFiles, errors };
    }

    private async getFileFromFileEntry(
        fileEntry: FileSystemFileEntry
    ): Promise<File> {
        return new Promise((resolve) => fileEntry.file(resolve));
    }

    private validateFileBeforeUpload(file: File): UploadValidationError[] {
        const errors: UploadValidationError[] = [];

        // Check file type
        if (!this.VALID_FILE_FORMATS.includes(file.type)) {
            console.error(`File ${file.name} has invalid type "${file.type}"`);
            errors.push({
                fileName: file.name,
                reason: 'invalidFormat',
            });
            return errors;
        }

        // Check file size (too large)
        if (file.size > this.MAX_FILE_SIZE) {
            console.error(`File ${file.name} is larger than 10MB`);
            errors.push({
                fileName: file.name,
                reason: 'fileTooLarge',
            });
            return errors;
        }

        // Check file size (empty)
        if (file.size === this.MIN_FILE_SIZE) {
            console.error(`File ${file.name} is empty`);
            errors.push({
                fileName: file.name,
                reason: 'invalidFormat',
            });
            return errors;
        }

        return errors;
    }

    private openErrorDialog(dialogData: ConfirmationDialogData) {
        this.matDialogConfig.data = dialogData;
        this.matDialog
            .open(ConfirmationDialogComponent, this.matDialogConfig)
            .afterClosed()
            .subscribe();
    }

    showFreePlanImageLimitExceededError(remainingImageCount: number) {
        this.openErrorDialog(
            createFreePlanImageLimitExceededErrorDialogData(remainingImageCount)
        );
    }

    showFreePlanUploadSelectionLimitExceededError() {
        this.openErrorDialog(
            createFreePlanUploadSelectionLimitExceededDialogData(
                FREE_PLAN_IMAGE_LIMIT
            )
        );
    }
}
